import React, { useCallback, useRef, useState } from 'react';
import { usePrevious } from '@mentimeter/react-hooks';
import { WordCloud, type WordCloudData } from '@mentimeter/visualizations';
import { SvgElement } from '@mentimeter/question-modules-shared';

export const arePropsEqual = (
  { data: prevData, ...prevProps }: WordCloudT,
  { data: nextData, ...nextProps }: WordCloudT,
) => {
  const prevKeys = Object.keys(prevProps) as (keyof typeof prevProps)[];
  const nextKeys = Object.keys(nextProps) as (keyof typeof nextProps)[];

  if (prevKeys.length !== nextKeys.length) {
    /* different amount of props, rerender */
    return false;
  }

  for (const key of prevKeys) {
    if (Array.isArray(prevProps[key]) && Array.isArray(nextProps[key])) {
      if (JSON.stringify(prevProps[key]) !== JSON.stringify(nextProps[key])) {
        // The arrays are not equal, rerender
        return false;
      }
    } else if (!Object.is(prevProps[key], nextProps[key])) {
      // a prop has changed, rerender
      return false;
    }
  }

  if (prevData.length !== nextData.length) {
    return false;
  }

  /* theoretically this can break. if you rerender from the words "foo","bar" to "foo","baz", it won't rerender: same amount of words with same length.
    However, in practice this won't happen. either the result set grows (votes coming in), or shrinks (moderation, which requires voting being closed) */
  const wordLengthIdentical =
    prevData.map((d) => d.text).join('').length ===
    nextData.map((d) => d.text).join('').length;
  const wordValueIdentical =
    prevData.map((d) => d.value).reduce((a: number, b) => a + (b ?? 0), 0) ===
    nextData.map((d) => d.value).reduce((a: number, b) => a + (b ?? 0), 0);

  return wordLengthIdentical && wordValueIdentical;
};

export interface WordCloudResultT {
  text: string;
  count: number;
}

export interface WordCloudT {
  selector: string;
  data: WordCloudData[];
  emojiCloud: boolean;
  font: string;
  shouldAnimate: boolean;
  isModerationEnabled: boolean;
  fillColors: string[];
  shouldShowResults: boolean;
  onRemoveWord: (key: string) => void;
}

// Leading debounce: it will call the callback as soon as possible, but not more than once every `delay` ms
function useDebounceFn(callback: () => void, delay: number): any {
  const [lastCall, setLastCall] = useState(0);
  const timeout = useRef<undefined | NodeJS.Timeout>(undefined);

  const debounceCallback = useCallback(() => {
    const now = Date.now();
    const timeSinceLastCall = now - lastCall;

    clearTimeout(timeout.current);

    if (timeSinceLastCall > delay) {
      callback();
      setLastCall(now);
    } else {
      const timeUntilNextCall = delay - timeSinceLastCall;
      timeout.current = setTimeout(
        () => {
          callback();
          setLastCall(now + timeUntilNextCall);
        },
        Math.max(0, timeUntilNextCall),
      );
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [callback, delay, lastCall]);

  // used to manually clear timer if the callback function has updated
  // we don't want an outdated callback to be called
  const clearTimer = useCallback(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, [timeout]);

  return { debounceCallback, clearTimer };
}

export const LegacyWordCloudVisualization = React.memo<WordCloudT>(
  ({
    selector,
    font,
    fillColors,
    shouldAnimate,
    data,
    emojiCloud,
    isModerationEnabled,
    shouldShowResults,
    onRemoveWord,
  }: WordCloudT) => {
    const prevShowResults = usePrevious(shouldShowResults);

    const animationMultiplier = shouldAnimate ? 1 : 0;

    const wordcloud = React.useMemo(() => {
      return new WordCloud({
        selector: `#${selector}`,
        data,
        emojiCloud,
        font,
        animationMultiplier,
        fillColors,
        moderationEnabled: isModerationEnabled,
        removeWord: onRemoveWord,
      });
    }, [
      animationMultiplier,
      selector,
      data,
      emojiCloud,
      font,
      fillColors,
      isModerationEnabled,
      onRemoveWord,
    ]);

    const { debounceCallback, clearTimer } = useDebounceFn(() => {
      wordcloud.update(shouldShowResults);
    }, 1800);

    React.useEffect(() => {
      const shouldUpdateImmediately = prevShowResults !== shouldShowResults;
      if (shouldUpdateImmediately) {
        clearTimer();
        wordcloud.update(shouldShowResults);
      } else {
        debounceCallback();
      }
      // debounceCallback is re-created on every call (because of the lastCall) so we don't want it as a dependency here
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clearTimer, prevShowResults, shouldShowResults, wordcloud.update]);

    return (
      <SvgElement aria-hidden>
        <g id={selector} />
      </SvgElement>
    );
  },
  arePropsEqual,
);
